import { useCallback } from "react";
import { Price } from "~/core/Price";
import {
  convertIso15897LocaleToBcp47Locale,
  expandLocaleToIso15897Locale,
} from "~/core/locale";
import { useLocale } from "~/core/useLocale";

type LocalizedPriceFormatterFunction = ({
  amount,
  currency,
}:
  | {
      amount: number;
      currency: string;
    }
  | Price) => string;

export const usePriceFormatter = (): {
  accountingPriceFormatter: LocalizedPriceFormatterFunction;
  marketingPriceFormatter: LocalizedPriceFormatterFunction;
} => {
  const locale = useLocale();

  const accountingPriceFormatter = useCallback<LocalizedPriceFormatterFunction>(
    ({ amount, currency }) =>
      amount.toLocaleString(
        convertIso15897LocaleToBcp47Locale(
          expandLocaleToIso15897Locale(locale),
        ),
        {
          style: "currency",
          currency: currency,
          currencyDisplay: "symbol",
          useGrouping: true,
        },
      ),
    [locale],
  );

  const marketingPriceFormatter = useCallback<LocalizedPriceFormatterFunction>(
    ({ amount, currency }) =>
      amount.toLocaleString(
        convertIso15897LocaleToBcp47Locale(
          expandLocaleToIso15897Locale(locale),
        ),
        {
          style: "currency",
          currency: currency,
          currencyDisplay: "symbol",
          useGrouping: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      ),
    [locale],
  );

  return {
    accountingPriceFormatter,
    marketingPriceFormatter,
  };
};
